import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormControlExFactory } from '../../../../core/service/factory/form-control-ex-factory.service';
import { Observable, Subject } from 'rxjs';
import { DocumentTypeMimeTypes } from '../../../../core/model/const/document-type-mime-types';
import { ChatService } from '../../../../common/service/chat-service';
import { CreateDialogMessageModel } from '../../../../common/model/dialog';
import { MultiFileUploadComponent } from '../../../../core/component/multi-file-upload/multi-file-upload.component';
import { MessageService } from '../../../../core/service/message.service';
import { SidebarService } from '../../../../core/service/sidebar.service';

@Component({
    selector: 'app-chat-input-message',
    templateUrl: './chat-input-message.component.html',
    styleUrls: ['./chat-input-message.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatInputMessageComponent implements OnInit {
  constructor(
    private readonly _fb: FormBuilder,
    private readonly _fc: FormControlExFactory,
    private readonly _chatService: ChatService,
    private readonly _messageService: MessageService,
    private readonly _sidebarService: SidebarService
  ) {}

  form: FormGroup;
  messageLengthLimit: number = 1000;
  header: string = '';
  message: string = '';
  visibleForTenant: boolean = false;
  invoicingTenant: boolean = false;
  isLoading: boolean = false;
  @Input() dialogId: string;
  @Input({ required: true }) dialogEndpoint!: string;
  @Input() visibleForTenantCheckbox: boolean;
  @Input() isTenantInvolved: boolean = true;
  @Output() messageSent = new EventEmitter();
  @ViewChild('fileUploadRef') childComponent: MultiFileUploadComponent;
  @ViewChild('attachmentsSidebarContent', { static: false }) attachmentsSidebarContent: TemplateRef<any>;

  get attachmentButtonDisabled(): boolean {
    return (this.form.controls['attachments'] as FormArray).controls.length >= this.maxFiles;
  }

  get sendButtonDisabled(): boolean {
    return !this.form.valid;
  }

  get messageLength(): number {
    return this.form.controls['message'].value.length;
  }

  get filesCount(): string {
    return (this.form.controls['attachments']?.value?.length ?? 0) + '/' + this.maxFiles;
  }

  attachmentDesktopClickSubject: Subject<void>;
  attachmentDesktopClickObservable$: Observable<void>;
  attachmentMobileClickSubject: Subject<void>;
  attachmentMobileClickObservable$: Observable<void>;
  maxFiles: number = 6;

  get mimes(): string[] {
    return DocumentTypeMimeTypes.PDF()
      .concat(DocumentTypeMimeTypes.WebImage())
      .concat(DocumentTypeMimeTypes.WordProcessor())
      .concat(DocumentTypeMimeTypes.SpreadSheet());
  }

  ngOnInit(): void {
    this.form = this.getFormGroup();

    if (!this.isTenantInvolved) {
      this.form.controls['visibleForTenant']?.setValue(false);
      this.form.controls['visibleForTenant']?.disable();
    } else if (!this.visibleForTenantCheckbox) {
      this.form.controls['visibleForTenant']?.disable();
    }

    this.attachmentDesktopClickSubject = new Subject<void>();
    this.attachmentDesktopClickObservable$ = this.attachmentDesktopClickSubject.asObservable();

    this.attachmentMobileClickSubject = new Subject<void>();
    this.attachmentMobileClickObservable$ = this.attachmentMobileClickSubject.asObservable();
  }

  protected getFormGroup(): FormGroup {
    return this._fb.group({
      header: this._fc.create('CHAT.MESSAGE_FORM.HEADER', this.header, Validators.maxLength(30)),
      message: this._fc.createMandatory('CHAT.MESSAGE_FORM.MESSAGE', this.message),
      visibleForTenant: this._fc.create('CHAT.MESSAGE_FORM.VISIBLE_FOR_TENANT', this.visibleForTenant),
      invoicingTenant: this._fc.create('CHAT.MESSAGE_FORM.INVOICING_TENANT', this.invoicingTenant),
      attachments: this._fb.array([]),
    });
  }

  onSubmit() {
    if (!this.form.valid) {
      return;
    }

    const createModel = new CreateDialogMessageModel();
    createModel.header = this.form.controls['header'].value;
    createModel.text = this.form.controls['message'].value;
    createModel.showToTenant = this.isTenantInvolved ? this.form.controls['visibleForTenant']?.value ?? false : false;
    createModel.dialogId = this.dialogId;

    createModel.attachments = (this.form.controls['attachments'] as FormArray).controls
      .map(f => f as FormGroup)
      .filter(f => f.controls['data'].value)
      .map(f => f.controls['data'].value);

    this.isLoading = true;
    this._chatService.createDialogMessage(createModel, this.dialogEndpoint).subscribe({
      next: () => {
        this.messageSent.emit();
        this._clearForm();
        this._messageService.showSuccess('CHAT.MESSAGE.MESSAGE_SENT');
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  private _clearForm() {
    this.form.controls['header'].setValue('');
    this.form.controls['message'].setValue('');
    this.form.controls['visibleForTenant'].setValue(false);
    this.childComponent.clearAllFiles();
  }

  onDesktopAttachmentClick() {
    this.attachmentDesktopClickSubject.next();
  }

  onMobileAttachmentClick() {
    this.attachmentMobileClickSubject.next();
  }

  onOpenAttachmentSidebarClick() {
    this._sidebarService.setSidebarContent({
      template: this.attachmentsSidebarContent,
    });
    this._sidebarService.toggleSidebar(true);
  }
}
